import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getAdminRequestsStats } from "../../../_services/adminRequests.service";
import styles from "./AdminRequestsStats.module.scss";

// Couleurs par type de demande (coherentes avec la charte HS).
const TYPE_COLORS = {
  calibration: "#134a6d",
  sav: "#1f6a99",
  support: "#5b9bc4",
};
const BAR_COLOR = "#134a6d";

// Construit la serie temporelle empilee a partir des lignes {month,type,count}.
const buildTimeline = (timeline = []) => {
  const map = new Map();
  timeline.forEach(({ month, type, count }) => {
    if (!map.has(month)) {
      map.set(month, { month, calibration: 0, sav: 0, support: 0 });
    }
    map.get(month)[type] = count;
  });
  return Array.from(map.values()).sort((a, b) =>
    a.month.localeCompare(b.month)
  );
};

const AdminRequestsStats = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("30d");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const data = await getAdminRequestsStats(period, signal);
        setStats(data);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(t("admin_requests.stats.load_error"));
      } finally {
        setLoading(false);
      }
    },
    [period, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const timelineData = useMemo(
    () => buildTimeline(stats?.timeline),
    [stats]
  );

  const typePieData = useMemo(() => {
    if (!stats?.kpi) return [];
    return [
      { name: t("admin_requests.type_calibration"), value: stats.kpi.calibration, key: "calibration" },
      { name: t("admin_requests.type_sav"), value: stats.kpi.sav, key: "sav" },
      { name: t("admin_requests.type_support"), value: stats.kpi.support, key: "support" },
    ].filter((d) => d.value > 0);
  }, [stats, t]);

  const periods = ["7d", "30d", "12m", "all"];

  return (
    <div className={styles.wrap}>
      {/* Selecteur de periode */}
      <div className={styles.periodBar}>
        {periods.map((p) => (
          <button
            key={p}
            type="button"
            className={`${styles.periodBtn} ${period === p ? styles.periodActive : ""}`}
            onClick={() => setPeriod(p)}
          >
            {t(`admin_requests.stats.period_${p}`)}
          </button>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>{t("admin_requests.loading")}</div>
      ) : !stats ? (
        <div className={styles.loading}>{t("admin_requests.empty")}</div>
      ) : (
        <>
          {/* KPI */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.total}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.stats.kpi_total")}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.calibration}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.type_calibration")}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.sav}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.type_sav")}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.support}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.type_support")}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.pdfAvailable}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.status_available")}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiValue}>{stats.kpi.archived}</span>
              <span className={styles.kpiLabel}>{t("admin_requests.status_archived")}</span>
            </div>
          </div>

          {/* Graphiques */}
          <div className={styles.chartsGrid}>
            {/* Evolution temporelle */}
            <div className={`${styles.chartCard} ${styles.wide}`}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.timeline_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="calibration" name={t("admin_requests.type_calibration")} stroke={TYPE_COLORS.calibration} strokeWidth={2} />
                  <Line type="monotone" dataKey="sav" name={t("admin_requests.type_sav")} stroke={TYPE_COLORS.sav} strokeWidth={2} />
                  <Line type="monotone" dataKey="support" name={t("admin_requests.type_support")} stroke={TYPE_COLORS.support} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Repartition par type */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.type_split_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={typePieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {typePieData.map((entry) => (
                      <Cell key={entry.key} fill={TYPE_COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top cabines */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.top_cabines_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.topCabines} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="cabine" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t("admin_requests.stats.count")} fill={BAR_COLOR} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top utilisateurs */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.top_users_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.topUsers} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t("admin_requests.stats.count")} fill={TYPE_COLORS.sav} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* SAV par materiel */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.sav_material_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.savByMaterial} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="material" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t("admin_requests.stats.count")} fill={TYPE_COLORS.calibration} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Support par categorie */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>{t("admin_requests.stats.support_category_title")}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.supportByCategory} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t("admin_requests.stats.count")} fill={TYPE_COLORS.support} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRequestsStats;
